import i18n from '@/utils/i18n';
import React from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { FormattedMarkdown } from '@/components/formatted-markdown';
import { Image, ScrollView, TextStyle, View } from 'react-native';
import { SCREEN_PADDING } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const BANNER_HEIGHT = 120;
const BANNER_HEIGHT_SMALL = 88;

interface Props {
  bannerImageUrl?: string | null;
  completionText?: string | null;
  onDone: () => void;
}

export const LoyaltyCompletionContent: React.FC<Props> = ({
  bannerImageUrl,
  completionText,
  onDone,
}) => {
  const [styles] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();

  const bannerBlock = bannerImageUrl ? (
    <Image
      accessibilityIgnoresInvertColors
      accessible={false}
      resizeMode="contain"
      source={{ uri: bannerImageUrl }}
      style={[
        styles.banner,
        { height: isSmallLayout ? BANNER_HEIGHT_SMALL : BANNER_HEIGHT },
      ]}
      testID="loyalty-completion-modal.banner"
    />
  ) : null;

  const markdownTextStyle = isSmallLayout
    ? styles.markdownSmall
    : styles.markdown;

  return (
    <>
      {bannerBlock}
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        style={styles.scroll}
      >
        {/* Fall back to generic copy so a program with no completion text
            does not render a bare Done button */}
        <FormattedMarkdown
          imageStyle={styles.markdownImage}
          markdown={completionText || i18n.t('loyalty.enrollment_complete')}
          textStyle={markdownTextStyle}
        />
      </ScrollView>
      <View
        style={[
          styles.footer,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
      >
        <Button
          onPress={onDone}
          trackingData={{ name: 'loyalty_completion_modal.done' }}
          type="primary"
        >
          {i18n.t('done')}
        </Button>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    width: '100%',
  },
  footer: {
    paddingHorizontal: DEFAULT_PADDING,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  markdown: {
    ...(theme.sk.typography.paragraph.md as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  markdownImage: {
    marginHorizontal: 'auto',
    maxWidth: 180,
  },
  markdownSmall: {
    ...(theme.sk.typography.paragraph.sm as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  scroll: {
    flexShrink: 1,
  },
  scrollContent: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: DEFAULT_PADDING,
  },
}));
